/**
 * session-state.ts — the portal's per-browser-session state keys, in ONE place
 * (Portal Access UAT fix F3, 6 July 2026 / Multi-Node Authentication Reference
 * §4.1 "switching context tears down the prior context's state").
 *
 * Sign-out must be a FULL teardown: the active-context selection, the L1
 * chooser's once-per-session marker, and any consent-gated impersonation
 * session all die with the sign-in they belonged to. Before this fix, sign-out
 * removed only the staff-impersonation flag — so the next sign-in (same tab)
 * skipped the "Where would you like to go?" chooser and could resume a prior
 * impersonation session. Every sign-out path calls clearPortalSessionState():
 * the navbar sign-out flow (NavBarProvider) and the useAuth Hub 'signedOut'
 * listener (the backstop for any other signOut() caller).
 */
import { clearMniSession } from './impersonation-session';
import { clearAllDefaultDestinations } from './default-destination';
import { clearActiveDomainPointers } from './active-domain-store';
import { clearRoutedAccount } from './routed-context';
import { clearActiveZone } from './zone-session';

// ⛔ `IMPERSONATING_KEY` ('nexa_impersonating') was REMOVED on 13 August 2026 with the legacy
// staff-admin impersonation path that was its only writer (OnboardPage's `?as=admin` branch).
// Nothing sets or reads it any more, so there is nothing left to tear down. The consent-gated
// (MNI) session below is a DIFFERENT mechanism and is still cleared here.
/** Once-per-session marker for the post-login L1 context chooser (ContextGate). */
export const CONTEXT_PROMPTED_KEY = 'nexa_ctx_prompted';
/** Resolved context count — read by the COG dropdown's "Switch context" row. */
export const CONTEXT_COUNT_KEY = 'nexa_contexts_count';

/** ADR-116 D-A4 — the person's own accounts that hold Partner status, as JSON.
 *  Read by the COG to drop "Become a Partner" from an account that already is
 *  one. Cleared with the rest of the session state on sign-out (below) — a stale
 *  list surviving a sign-in as somebody else would hide the row for a user whose
 *  account is not a partner at all. */
export const PARTNER_ACCOUNTS_KEY = 'nexa_partner_accounts';

/**
 * ADR-122 Part B — the DATA REGION this account's records live in, as the server named it.
 *
 * ⭐⭐ THIS IS THE SERVER'S ANSWER, NOT `zone-session.ts`'s, AND THE DIFFERENCE MATTERS. That
 * module caches the zone this BROWSER resolved for sign-in routing — which pool to authenticate
 * against. This is the zone whose Lambda actually answered `/portal/me`, i.e. where the
 * account's data is. In practice the two agree, and when they cannot, the one that answered is
 * the one telling the truth about residency. A data-sovereignty claim shown to a customer is
 * the wrong place to display a client-side cache of a routing decision.
 *
 * Stored as the display LABEL the server resolved (e.g. "Australia"), so adding a zone stays a
 * deploy rather than a bundle change — see `zoneDisplayName` in `shared/src/region-routing.ts`.
 *
 * Mirrored to sessionStorage for the same reason the context count and partner accounts are:
 * the COG dropdown renders without its own `/portal/me` round-trip. Cleared on sign-out below,
 * because a stale region surviving a sign-in as somebody else would show one customer another
 * customer's jurisdiction.
 */
export const DATA_REGION_KEY = 'nexa_data_region';

/**
 * ⭐⭐ "THE SET OF ACCOUNTS I CAN REACH JUST CHANGED" — dispatch after any action
 * that grants or ends the caller's OWN reach (ADR-116, 12 August 2026).
 *
 * ⛔⛔ THE DEFECT THIS EXISTS FOR. `auth.consultantAccounts` is resolved once, by
 * the `/portal/me` call at page load. A consultant who ACCEPTS an invitation has
 * changed that list server-side and holds a stale copy — so the very next click,
 * "Open", was refused by `RouteGuard` against a list that predated the acceptance
 * by two seconds. Caught by the E2E harness (assertion 6.2) immediately after the
 * guard's fail-open was closed; before that, the stale list was never consulted
 * because the guard skipped itself entirely.
 *
 * ⚠ FIX THE PRODUCER, NOT THE GUARD. Making the guard tolerate a stale list would
 * put the fail-open back under a new name. The list is what is wrong, so the list
 * is what is refreshed — `useAuth` re-runs its resolution on this event, exactly
 * as it already does for `nexa:brand-updated`.
 */
export const ACCESS_UPDATED_EVENT = 'nexa:access-updated';

/**
 * Tear down every piece of per-sign-in session state: the consent-gated (MNI)
 * impersonation session, the chooser's prompted marker, the context count, and
 * the persisted active-domain pointers.
 * Idempotent and safe in private mode.
 *
 * The active-domain pointers (15 July 2026) are the one piece of this state that
 * lives in localStorage, not sessionStorage — so before this fix they were the
 * only per-sign-in state that SURVIVED sign-out, which is why signing out and back
 * in did not clear a wrong active domain. They are now torn down with the rest.
 */
export function clearPortalSessionState(): void {
  clearMniSession();
  clearActiveDomainPointers();
  // ADR-113 D22 — the sign-in destination default belongs to the sign-in that
  // set it. A shared device must not carry one person's chosen destination into
  // the next person's session, so it dies with the session exactly as the
  // impersonation marker and the active-domain pointers already do.
  clearAllDefaultDestinations();
  // The account this tab was standing in belongs to the sign-in that chose it —
  // carrying it into the next sign-in would send a different person's `/` to an
  // account they may not hold (it would be rejected and land them on primary, but
  // the right time to forget it is now). See `routed-context.ts`.
  clearRoutedAccount();
  // ADR-120 Amendment 4 — the resolved zone belongs to the sign-in that resolved it. On a
  // shared device, carrying one person's zone into the next person's session would point
  // Amplify at a pool that does not hold them, and their correct password would be refused.
  // Forgetting it returns the next visitor to this bundle's own zone, which is where a
  // fresh browser starts and what every sign-in did before runtime resolution existed.
  //
  // ⚠ SAFE DESPITE `signInFresh` CALLING THIS ON ITS WAY IN: that function clears FIRST and
  // resolves SECOND, precisely so this line cannot wipe the zone it is about to need.
  clearActiveZone();
  for (const key of [CONTEXT_PROMPTED_KEY, CONTEXT_COUNT_KEY, PARTNER_ACCOUNTS_KEY, DATA_REGION_KEY]) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // private mode — nothing stored, nothing to clear
    }
  }
}
